import styles from "./Footer.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
} from "react-icons/fa";
import { 
  FiMail, FiPhone, FiMapPin, 
  FiHome, FiUser, FiBriefcase, FiMessageSquare, 
  FiAward, FiGlobe, FiChevronRight 
} from "react-icons/fi";
import bvLogo from "../../assets/bv.jpg";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = () => location.pathname === "/";

  const scrollToSection = (sectionId) => {
    if (isHomePage()) {
      const event = new CustomEvent('scrollToSection', { 
        detail: { section: sectionId } 
      });
      window.dispatchEvent(event);
    } else {
      navigate("/");
      setTimeout(() => {
        const event = new CustomEvent('scrollToSection', { 
          detail: { section: sectionId } 
        });
        window.dispatchEvent(event);
      }, 500);
    }
  };

  const footerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  // Quick links
  const quickLinks = [
    { name: "Home", icon: FiHome, section: "home" },
    { name: "About", icon: FiUser, section: "about" },
    { name: "Services", icon: FiBriefcase, section: "services" },
    { name: "StudioX", icon: FiAward, section: "studiox" },
    { name: "Testimonials", icon: FiMessageSquare, section: "testimonials" }
    
  ];

  // Services links
  const servicesLinks = [
    "Web Development",
    "Mobile Development", 
    "Enterprise Software",
    "E-commerce Solutions",
    "AI & ML Solutions"
  ];

  return (
    <motion.footer 
      className={styles.footer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={footerVariants}
    >
      <div className={styles.container}>
        {/* Main Footer Content */}
        <div className={styles.footerContent}>
          {/* Brand Section */}
          <motion.div className={styles.brandSection} variants={itemVariants}>
            <div className={styles.logoWrapper}>
              <img 
                src={bvLogo} 
                alt="BrandVerse Logo" 
                className={styles.brandLogo}
                onClick={() => scrollToSection('home')}
              />
            </div>
            <p className={styles.brandDescription}>
              Bridging creativity and technology to deliver intelligent digital solutions. 
              We empower businesses with AI-driven platforms that enhance growth and innovation.
            </p>
            <div className={styles.trustBadges}>
              <img src="/images/certificates/startup-india-logo.png" alt="Startup India" />
              <img src="/images/certificates/msme-logo.png" alt="MSME" />
              <img src="/images/stamp.jpg" alt="Company Stamp" className={styles.stampImage} />
            </div>
          </motion.div>

          {/* Links Container - For side by side layout on mobile */}
          <div className={styles.linksContainer}>
            {/* Quick Links */}
            <motion.div className={styles.linksSection} variants={itemVariants}>
              <h3 className={styles.sectionTitle}>Quick Links</h3>
              <ul className={styles.linksList}>
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      className={styles.linkButton}
                      onClick={() => scrollToSection(link.section)}
                    >
                      <link.icon className={styles.linkIcon} />
                      <span>{link.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div className={styles.servicesSection} variants={itemVariants}>
              <h3 className={styles.sectionTitle}>Our Services</h3>
              <ul className={styles.servicesList}>
                {servicesLinks.map((service, index) => (
                  <li key={index}>
                    <button
                      className={styles.serviceButton}
                      onClick={() => scrollToSection('services')}
                    >
                      <FiChevronRight className={styles.serviceIcon} />
                      <span>{service}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact & Social */}
          <motion.div className={styles.contactSection} variants={itemVariants}>
            <h3 className={styles.sectionTitle}>Get In Touch</h3>
            
            <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <FiMapPin className={styles.contactIcon} />
                <span>Vijayawada, Andhra Pradesh, India</span>
              </div>
              <div className={styles.contactItem}>
                <FiMail className={styles.contactIcon} />
                <a href="mailto:contact@brandversetech.com">contact@brandversetech.com</a>
              </div>
              <div className={styles.contactItem}>
                <FiPhone className={styles.contactIcon} />
                <a href="tel:+919154884214">+91 9154884214</a>
              </div>
            </div>

            {/* Social Links */}
            <div className={styles.socialSection}>
              <div className={styles.socialIcons}>
                <a
                  href="https://www.instagram.com/brandverse_technologies/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://www.linkedin.com/company/brandverse-official/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn />
                </a>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label="Twitter"
                >
                  <FaTwitter />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Footer */}
        <div className={styles.bottomFooter}>
          <p className={styles.copyright}>
            © {new Date().getFullYear()} Brandverse Technologies India Pvt. Ltd. All rights reserved.
          </p>
          <div className={styles.legalLinks}>
            <button 
  className={styles.legalLink}
  onClick={() => navigate('/privacy-policy')}
>
  Privacy Policy
</button>
<button 
  className={styles.legalLink}
  onClick={() => navigate('/terms-of-service')}
>
  Terms of Service
</button>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;